import { createContext, useCallback, useContext, useState, type ComponentType } from 'react';

export interface DialogProps<TPayload = undefined, TResult = void> {
  open: boolean;
  onClose: (result: TResult) => void;
  payload: TPayload;
}

interface DialogEntry {
  key: number;
  Component: ComponentType<DialogProps<any, any>>;
  payload: any;
  resolve: (result: any) => void;
}

interface DialogsContextValue {
  open: <TPayload, TResult>(
    Component: ComponentType<DialogProps<TPayload, TResult>>,
    payload?: TPayload,
  ) => Promise<TResult>;
}

const DialogsContext = createContext<DialogsContextValue | null>(null);

let nextKey = 0;

export function DialogsProvider({ children }: { children: React.ReactNode }) {
  const [dialogs, setDialogs] = useState<DialogEntry[]>([]);

  const open = useCallback(
    <TPayload, TResult>(
      Component: ComponentType<DialogProps<TPayload, TResult>>,
      payload?: TPayload,
    ): Promise<TResult> => {
      return new Promise<TResult>((resolve) => {
        const key = nextKey++;
        setDialogs((prev) => [
          ...prev,
          { key, Component, payload, resolve },
        ]);
      });
    },
    [],
  );

  const handleClose = useCallback((key: number, result: any) => {
    setDialogs((prev) => {
      const entry = prev.find((d) => d.key === key);
      entry?.resolve(result);
      return prev.filter((d) => d.key !== key);
    });
  }, []);

  return (
    <DialogsContext.Provider value={{ open }}>
      {children}
      {dialogs.map(({ key, Component, payload }) => (
        <Component
          key={key}
          open
          payload={payload}
          onClose={(result: any) => handleClose(key, result)}
        />
      ))}
    </DialogsContext.Provider>
  );
}

export function useDialogs(): DialogsContextValue {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error('useDialogs must be used within DialogsProvider');
  return ctx;
}
